import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { FaSyncAlt } from 'react-icons/fa';

export default function UpdateAvailableModal() {
  const [registration, setRegistration] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    Modal.setAppElement('#root');
  }, []);

  useEffect(() => {
    const handleUpdate = (e) => {
      setRegistration(e.detail);
      setDismissed(false);
    };

    window.addEventListener('sw-update-available', handleUpdate);

    // Check for a waiting service worker on load
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        if (reg.waiting) {
          setRegistration(reg);
        }
      });
    }

    return () => {
      window.removeEventListener('sw-update-available', handleUpdate);
    };
  }, []);

  const onUpdate = () => {
    if (!registration) return;
    setUpdating(true);

    const waitingWorker = registration.waiting;
    if (waitingWorker) {
      // Set up the listener to reload the page once the new service worker takes over
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    } else {
      // Fallback reload
      window.location.reload();
    }
  };

  const onDismiss = () => {
    setDismissed(true);
  };

  const isOpen = Boolean(registration) && !dismissed;

  return (
    <>
      <style>{`
        @keyframes rotate-slow {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse-soft {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.08); opacity: 0.95; }
        }
        @keyframes slide-up {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .sw-update-modal-content {
          animation: slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          outline: none;
        }
        .sw-update-rotate {
          animation: rotate-slow 4s linear infinite;
        }
        .sw-update-icon-container {
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          color: #2563eb;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          margin-bottom: 16px;
          font-size: 24px;
          box-shadow: 0 4px 14px rgba(37, 99, 235, 0.15);
          animation: pulse-soft 2.5s ease-in-out infinite;
        }
        .sw-update-button-primary {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          color: white;
          border: none;
          padding: 10px 20px;
          font-weight: 600;
          border-radius: 10px;
          cursor: pointer;
          font-size: 14px;
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .sw-update-button-primary:hover {
          transform: translateY(-1.5px);
          box-shadow: 0 6px 16px rgba(37, 99, 235, 0.35);
          background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
        }
        .sw-update-button-primary:active {
          transform: translateY(0);
        }
        .sw-update-button-secondary {
          background: #ffffff;
          color: #4b5563;
          border: 1px solid #e5e7eb;
          padding: 10px 20px;
          font-weight: 500;
          border-radius: 10px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s ease;
        }
        .sw-update-button-secondary:hover {
          background: #f9fafb;
          border-color: #d1d5db;
          color: #1f2937;
        }
      `}</style>

      <Modal
        isOpen={isOpen}
        onRequestClose={onDismiss}
        contentLabel="App Update Available"
        style={{
          overlay: {
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
            backdropFilter: 'blur(8px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          },
          content: {
            position: 'relative',
            width: '100%',
            maxWidth: '400px',
            background: '#ffffff',
            borderRadius: '16px',
            border: '1px solid rgba(0,0,0,0.05)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            padding: '24px',
            inset: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
          },
        }}
        className="sw-update-modal-content"
      >
        <div className="sw-update-icon-container">
          <FaSyncAlt className="sw-update-rotate" style={{ animationDuration: updating ? '1s' : '4s' }} />
        </div>

        <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1f2937', margin: '0 0 8px 0' }}>
          Update Available
        </h3>
        
        <p style={{ fontSize: '14px', color: '#4b5563', lineHeight: 1.5, margin: '0 0 24px 0' }}>
          A new version of CMI PMS is ready! Update now to experience the latest improvements and features.
        </p>

        <div style={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'stretch' }}>
          <button 
            onClick={onDismiss} 
            className="sw-update-button-secondary"
            style={{ flex: 1 }}
            disabled={updating}
          >
            Later
          </button>
          <button 
            onClick={onUpdate} 
            className="sw-update-button-primary"
            style={{ flex: 1 }}
            disabled={updating}
          >
            {updating ? 'Updating...' : 'Update Now'}
          </button>
        </div>
      </Modal>
    </>
  );
}
